import { useEffect, useReducer, useRef } from 'react'
import { message } from 'antd'
import { useLocation } from 'react-router-dom'
import {
  GET_SELF_CONTRACT_PAGE,
  GET_SELF_CONTRACT_ATTACHMENT_STATUS,
  IMPORT_SELF_CONTRACT_FILE,
  DOWNLOAD_SELF_CONTRACT_FILE,
} from '@/api/selfAgreementApi'
import { FrontSelfContractListVO } from '@/api/selfAgreementApi/response'
import { FrontSelfContractPageParam } from '@/api/selfAgreementApi/request'
import { fileFile } from '@/modules/uploadfiles/hooks'
import {
  downloadFile as downloadByUrl,
  isResponseCodeSuccess,
} from '@/tools/util'
import { usePageCache, useSkipInitEffect } from '@/tools/utilHooks'
import {
  initialState,
  reducer,
  setDataSourceAction,
  setDownloadLoadingAction,
  setFileDetailAction,
  setFileListAction,
  setIsTableLoading,
  setPaginationOptionAction,
  setSearchParamAction,
  setSelectedRowKeysAction,
  setSelectedRowsAction,
  setShowListAction,
  reloadStateAction,
} from './reducer'
export function useSetupHook() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { paginationOption, searchParam, selectedRowKeys, agreementId } = state
  const { setCacheWithKey, getCacheWithKey } = usePageCache()
  const { pathname } = useLocation()
  const searchRef = useRef(null)
  const uploadRef = useRef<HTMLElement>(null)
  const needRefresh = useRef<boolean>(false)
  const CACHE_KEY = pathname

  useEffect(() => {
    const flag = getCacheWithKey(CACHE_KEY)
    if (flag) {
      dispatch(reloadStateAction(flag))
    } else {
      handlePaginationChange(1)
    }
    // eslint-disable-next-line
  }, [])

  useSkipInitEffect(() => {
    setCacheWithKey(state, CACHE_KEY)
    // eslint-disable-next-line
  }, [state])

  async function getPageData(params: FrontSelfContractPageParam) {
    const {
      pageNo = paginationOption.current,
      pageSize = paginationOption.pagesize,
    } = params
    dispatch(setIsTableLoading(true))
    try {
      const res = await GET_SELF_CONTRACT_PAGE({
        ...params,
        pageSize,
      })
      if (isResponseCodeSuccess(res.code)) {
        dispatch(setDataSourceAction(res.data.content))
        dispatch(
          setPaginationOptionAction({
            pagesize: pageSize,
            current: pageNo,
            total: res.data.totalCount,
          }),
        )
      }
    } finally {
      dispatch(setIsTableLoading(false))
    }
  }

  async function handlePaginationChange(page: number, pageSize?: number) {
    await getPageData({
      pageNo: page,
      pageSize: pageSize ? pageSize : paginationOption.pagesize,
      ...searchParam,
    })
  }
  const handleTableChangeCheck = (
    rowKeys: string[],
    rows: FrontSelfContractListVO[],
  ) => {
    dispatch(setSelectedRowsAction(rows))
    dispatch(setSelectedRowKeysAction(rowKeys))
  }

  function resetSelection() {
    dispatch(setSelectedRowsAction([]))
    dispatch(setSelectedRowKeysAction([]))
  }

  const handleSearchClick = (value: Record<string, unknown>) => {
    getPageData({
      pageNo: 1,
      pageSize: paginationOption.pagesize,
      ...value,
    })
    dispatch(setSearchParamAction(value))
    resetSelection()
  }

  async function handleRefreshData() {
    getPageData({
      pageNo: paginationOption.current,
      pageSize: paginationOption.pagesize,
      ...searchParam,
    })
  }

  // 下载协议附件：提交批量下载任务到任务中心，前端不处理文件流
  const downloadFile = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选数据')
      return
    }
    if (state.downloadLoading) {
      return
    }
    dispatch(setDownloadLoadingAction(true))
    try {
      const res = await DOWNLOAD_SELF_CONTRACT_FILE({ ids: selectedRowKeys })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '已提交批量下载任务到SRM任务中心')
      }
    } finally {
      dispatch(setDownloadLoadingAction(false))
    }
  }

  /** 按附件链接拉取内容并触发本地下载（非 window.open） */
  const handleDownloadPendingFile = (url: string, fileName?: string) => {
    if (!url) {
      return
    }
    const name =
      fileName ||
      decodeURIComponent(url.split('/').pop()?.split('?')[0] || '') ||
      '待签署文件.pdf'
    downloadByUrl(url, name)
  }

  // 查询协议附件审核状态（弹窗）
  const getFileStatus = async (id: string) => {
    const res = await GET_SELF_CONTRACT_ATTACHMENT_STATUS(id)
    if (isResponseCodeSuccess(res.code)) {
      dispatch(
        setFileDetailAction({
          fileStatus: res.data,
          fileList: res.data.attachmentFile
            ? [res.data.attachmentFile as fileFile]
            : [],
          reason: res.data.fileReviewOpinion ?? '',
        }),
      )
    }
  }

  const getAttachment = (record: FrontSelfContractListVO) => {
    dispatch(
      setShowListAction({ showList: true, agreementId: record.id ?? '' }),
    )
    getFileStatus(record.id ?? '')
  }

  // 表格内首次上传（提交 H0 审核，body 透传上传返回的 UpFileDTO）
  const inTableUploadfile = async (options: {
    e: fileFile[]
    record: FrontSelfContractListVO
  }) => {
    const { e } = options
    if (!e.length) {
      // 删除走组件内部接口，刷新即可
      message.destroy()
      await handleRefreshData()
      return
    }
    const hide = message.loading('文件上传中...', 0)
    try {
      const f = e[0]
      const res = await IMPORT_SELF_CONTRACT_FILE({
        id: f.id as string,
        name: f.name,
        url: f.url,
        baseUrl: f.baseUrl,
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '上传成功，协议附件已提交审核')
      }
    } finally {
      hide()
    }
    await handleRefreshData()
  }

  // 弹窗内上传（含驳回后重传；后端归档逻辑不依赖 resubmit）
  const fileListChange = async (e: fileFile[]) => {
    dispatch(setFileListAction(e))
    if (!e[0]) {
      return
    }
    needRefresh.current = true
    const hide = message.loading('文件上传中...', 0)
    try {
      const f = e[0]
      const res = await IMPORT_SELF_CONTRACT_FILE({
        id: f.id as string,
        name: f.name,
        url: f.url,
        baseUrl: f.baseUrl,
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '重新上传成功，协议附件已提交审核')
        await getFileStatus(agreementId)
      }
    } finally {
      hide()
    }
  }

  /** 关闭附件弹窗并清空详情，避免下次打开闪旧数据 */
  const clearAttachmentModalState = () => {
    dispatch(setShowListAction({ showList: false, agreementId: '' }))
    dispatch(
      setFileDetailAction({
        fileStatus: undefined,
        fileList: [],
        reason: '',
      }),
    )
  }

  const cancelUpload = () => {
    if (needRefresh.current) {
      handleRefreshData()
      needRefresh.current = false
    }
    clearAttachmentModalState()
  }

  const confirmUpload = () => {
    if (needRefresh.current) {
      handleRefreshData()
      needRefresh.current = false
    }
    clearAttachmentModalState()
  }

  return {
    state,
    searchRef,
    uploadRef,
    handlePaginationChange,
    handleTableChangeCheck,
    handleSearchClick,
    downloadFile,
    getAttachment,
    inTableUploadfile,
    fileListChange,
    cancelUpload,
    confirmUpload,
    handleDownloadPendingFile,
  }
}
import { useEffect, useReducer, useRef } from 'react'
import { message } from 'antd'
import { useLocation } from 'react-router-dom'
import {
  GET_SELF_CONTRACT_PAGE,
  GET_SELF_CONTRACT_ATTACHMENT_STATUS,
  IMPORT_SELF_CONTRACT_FILE,
  DOWNLOAD_SELF_CONTRACT_FILE,
} from '@/api/selfAgreementApi'
import { FrontSelfContractListVO } from '@/api/selfAgreementApi/response'
import { FrontSelfContractPageParam } from '@/api/selfAgreementApi/request'
import { fileFile } from '@/modules/uploadfiles/hooks'
import {
  downloadFile as downloadByUrl,
  isResponseCodeSuccess,
} from '@/tools/util'
import { usePageCache, useSkipInitEffect } from '@/tools/utilHooks'
import {
  initialState,
  reducer,
  setDataSourceAction,
  setDownloadLoadingAction,
  setFileDetailAction,
  setFileListAction,
  setIsTableLoading,
  setPaginationOptionAction,
  setSearchParamAction,
  setSelectedRowKeysAction,
  setSelectedRowsAction,
  setShowListAction,
  reloadStateAction,
} from './reducer'
export function useSetupHook() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { paginationOption, searchParam, selectedRowKeys, agreementId } = state
  const { setCacheWithKey, getCacheWithKey } = usePageCache()
  const { pathname } = useLocation()
  const searchRef = useRef(null)
  const uploadRef = useRef<HTMLElement>(null)
  const needRefresh = useRef<boolean>(false)
  const CACHE_KEY = pathname

  useEffect(() => {
    const flag = getCacheWithKey(CACHE_KEY)
    if (flag) {
      dispatch(reloadStateAction(flag))
    } else {
      handlePaginationChange(1)
    }
    // eslint-disable-next-line
  }, [])

  useSkipInitEffect(() => {
    setCacheWithKey(state, CACHE_KEY)
    // eslint-disable-next-line
  }, [state])

  async function getPageData(params: FrontSelfContractPageParam) {
    const {
      pageNo = paginationOption.current,
      pageSize = paginationOption.pagesize,
    } = params
    dispatch(setIsTableLoading(true))
    try {
      const res = await GET_SELF_CONTRACT_PAGE({
        ...params,
        pageSize,
      })
      if (isResponseCodeSuccess(res.code)) {
        dispatch(setDataSourceAction(res.data.content))
        dispatch(
          setPaginationOptionAction({
            pagesize: pageSize,
            current: pageNo,
            total: res.data.totalCount,
          }),
        )
      }
    } finally {
      dispatch(setIsTableLoading(false))
    }
  }

  async function handlePaginationChange(page: number, pageSize?: number) {
    await getPageData({
      pageNo: page,
      pageSize: pageSize ? pageSize : paginationOption.pagesize,
      ...searchParam,
    })
  }
  const handleTableChangeCheck = (
    rowKeys: string[],
    rows: FrontSelfContractListVO[],
  ) => {
    dispatch(setSelectedRowsAction(rows))
    dispatch(setSelectedRowKeysAction(rowKeys))
  }

  function resetSelection() {
    dispatch(setSelectedRowsAction([]))
    dispatch(setSelectedRowKeysAction([]))
  }

  const handleSearchClick = (value: Record<string, unknown>) => {
    getPageData({
      pageNo: 1,
      pageSize: paginationOption.pagesize,
      ...value,
    })
    dispatch(setSearchParamAction(value))
    resetSelection()
  }

  async function handleRefreshData() {
    getPageData({
      pageNo: paginationOption.current,
      pageSize: paginationOption.pagesize,
      ...searchParam,
    })
  }

  // 下载协议附件：提交批量下载任务到任务中心，前端不处理文件流
  const downloadFile = async () => {
    if (!selectedRowKeys.length) {
      message.warning('请先勾选数据')
      return
    }
    if (state.downloadLoading) {
      return
    }
    dispatch(setDownloadLoadingAction(true))
    try {
      const res = await DOWNLOAD_SELF_CONTRACT_FILE({ ids: selectedRowKeys })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '已提交批量下载任务到SRM任务中心')
      }
    } finally {
      dispatch(setDownloadLoadingAction(false))
    }
  }

  /** 按附件链接拉取内容并触发本地下载（非 window.open） */
  const handleDownloadPendingFile = (url: string, fileName?: string) => {
    if (!url) {
      return
    }
    const name =
      fileName ||
      decodeURIComponent(url.split('/').pop()?.split('?')[0] || '') ||
      '待签署文件.pdf'
    downloadByUrl(url, name)
  }

  // 查询协议附件审核状态（弹窗）
  const getFileStatus = async (id: string) => {
    const res = await GET_SELF_CONTRACT_ATTACHMENT_STATUS(id)
    if (isResponseCodeSuccess(res.code)) {
      dispatch(
        setFileDetailAction({
          fileStatus: res.data,
          fileList: res.data.attachmentFile
            ? [res.data.attachmentFile as fileFile]
            : [],
          reason: res.data.fileReviewOpinion ?? '',
        }),
      )
    }
  }

  const getAttachment = (record: FrontSelfContractListVO) => {
    dispatch(
      setShowListAction({ showList: true, agreementId: record.id ?? '' }),
    )
    getFileStatus(record.id ?? '')
  }

  // 表格内首次上传（提交 H0 审核，body 透传上传返回的 UpFileDTO）
  const inTableUploadfile = async (options: {
    e: fileFile[]
    record: FrontSelfContractListVO
  }) => {
    const { e } = options
    if (!e.length) {
      // 删除走组件内部接口，刷新即可
      message.destroy()
      await handleRefreshData()
      return
    }
    const hide = message.loading('文件上传中...', 0)
    try {
      const f = e[0]
      const res = await IMPORT_SELF_CONTRACT_FILE({
        id: f.id as string,
        name: f.name,
        url: f.url,
        baseUrl: f.baseUrl,
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '上传成功，协议附件已提交审核')
      }
    } finally {
      hide()
    }
    await handleRefreshData()
  }

  // 弹窗内上传（含驳回后重传；后端归档逻辑不依赖 resubmit）
  const fileListChange = async (e: fileFile[]) => {
    dispatch(setFileListAction(e))
    if (!e[0]) {
      return
    }
    needRefresh.current = true
    const hide = message.loading('文件上传中...', 0)
    try {
      const f = e[0]
      const res = await IMPORT_SELF_CONTRACT_FILE({
        id: f.id as string,
        name: f.name,
        url: f.url,
        baseUrl: f.baseUrl,
      })
      if (isResponseCodeSuccess(res.code)) {
        message.success(res.msg || '重新上传成功，协议附件已提交审核')
        await getFileStatus(agreementId)
      }
    } finally {
      hide()
    }
  }

  /** 关闭附件弹窗并清空详情，避免下次打开闪旧数据 */
  const clearAttachmentModalState = () => {
    dispatch(setShowListAction({ showList: false, agreementId: '' }))
    dispatch(
      setFileDetailAction({
        fileStatus: undefined,
        fileList: [],
        reason: '',
      }),
    )
  }

  const cancelUpload = () => {
    if (needRefresh.current) {
      handleRefreshData()
      needRefresh.current = false
    }
    clearAttachmentModalState()
  }

  const confirmUpload = () => {
    if (needRefresh.current) {
      handleRefreshData()
      needRefresh.current = false
    }
    clearAttachmentModalState()
  }

  return {
    state,
    searchRef,
    uploadRef,
    handlePaginationChange,
    handleTableChangeCheck,
    handleSearchClick,
    downloadFile,
    getAttachment,
    inTableUploadfile,
    fileListChange,
    cancelUpload,
    confirmUpload,
    handleDownloadPendingFile,
  }
}
